import React from "react";
import {Box, Grid} from "@mui/material";
import {visuallyHidden} from "@mui/utils";
import {
    ANavigationTotalizer,
} from "@atiautomacao/ati-ui-library";
import SolarPowerOutlinedIcon from "@mui/icons-material/SolarPowerOutlined";
import EnergySavingsLeafOutlinedIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import OnlinePredictionIcon from "@mui/icons-material/OnlinePrediction";

export const HINT_NORMALIZED_POWER_STATIONS =
    "Quantidade de usinas sem eventos de falha de comunicação ou alarmes ativos";

export const HINT_GENERATED_ENERGY =
    "Produção no período comparado com a Energia Esperada corrigida pela irradiação.";

export const HINT_AVAILABILITY =
    "Disponibilidade média dos inversores das usinas, expurgando periodos de ausência de informação de irradiancia POA.";

export const PLACEHOLDER = "-";
export const RATIO_PLACEHOLDER = "-/-";

export type DailyViewTotalizersDisplay = {
    normalizedRatio?: React.ReactNode;
    normalizedPowerStations?: React.ReactNode;
    totalPowerStations?: React.ReactNode;
    generatedEnergy?: React.ReactNode;
    expectedEnergy?: React.ReactNode;
    criticalAlarms?: React.ReactNode;
    equipmentNoCommunication?: React.ReactNode;
    availabilityPercent?: React.ReactNode;
};

export type DailyViewTotalizersNavigationVisibility = {
    synoptic: boolean;
    performance: boolean;
    alarms: boolean;
    unavailability: boolean;
};

export type DailyViewTotalizersProps = {
    display?: DailyViewTotalizersDisplay;
    loading?: boolean;
    navigationVisibility: DailyViewTotalizersNavigationVisibility;
    onNavigateSynoptic?: () => void;
    onNavigatePerformance?: () => void;
    onNavigateAlarms?: () => void;
    onNavigateUnavailability?: () => void;
};

const noop = () => undefined;

export default function DailyViewTotalizers({
    display,
    loading = false,
    onNavigateSynoptic = noop,
    onNavigatePerformance = noop,
    onNavigateAlarms = noop,
    onNavigateUnavailability = noop,
    navigationVisibility,
}: DailyViewTotalizersProps) {
    const normalizedRatio = display?.normalizedRatio ?? RATIO_PLACEHOLDER;
    const generatedEnergy = display?.generatedEnergy ?? PLACEHOLDER;
    const expectedEnergy = display?.expectedEnergy ?? PLACEHOLDER;
    const criticalAlarms = display?.criticalAlarms ?? PLACEHOLDER;
    const equipmentNoCommunication = display?.equipmentNoCommunication ?? PLACEHOLDER;
    const availabilityPercent = display?.availabilityPercent ?? PLACEHOLDER;

    const hasNormalizedPair =
        display?.normalizedPowerStations !== undefined && display?.totalPowerStations !== undefined;
    const normalizedValue = hasNormalizedPair ? (
        <>
            <span aria-hidden="true">{normalizedRatio}</span>
            <Box component="span" sx={visuallyHidden}>
                {display?.normalizedPowerStations} de {display?.totalPowerStations} usinas normalizadas
            </Box>
        </>
    ) : normalizedRatio;

    const hasEnergyPair = display?.generatedEnergy !== undefined || display?.expectedEnergy !== undefined;
    const energyValue = hasEnergyPair ? (
        <>
            <span aria-hidden="true">{generatedEnergy} / {expectedEnergy}</span>
            <Box component="span" sx={visuallyHidden}>
                {generatedEnergy} MWh gerados de {expectedEnergy} MWh esperados
            </Box>
        </>
    ) : (
        <>{generatedEnergy} / {expectedEnergy}</>
    );

    return (
        <Grid
            container
            spacing={3}
            data-testid={loading ? "daily-view-totalizers-loading" : "daily-view-totalizers"}
            aria-busy={loading || undefined}
        >
            <Grid item xs={12} lg={6} xl={3}>
                <ANavigationTotalizer
                    loading={loading}
                    icon={<SolarPowerOutlinedIcon/>}
                    tone="success"
                    title="Usinas Normalizadas"
                    infoHint={HINT_NORMALIZED_POWER_STATIONS}
                    value={normalizedValue}
                    unit="usinas"
                    description="Sem alarmes críticos"
                    navigation={{
                        label: "VER SINÓPTICO",
                        onClick: onNavigateSynoptic,
                        visible: navigationVisibility.synoptic,
                    }}
                />
            </Grid>
            <Grid item xs={12} lg={6} xl={3}>
                <ANavigationTotalizer
                    loading={loading}
                    icon={<EnergySavingsLeafOutlinedIcon/>}
                    tone="primary"
                    title="Energia Gerada"
                    infoHint={HINT_GENERATED_ENERGY}
                    value={energyValue}
                    unit="MWh"
                    description="Produção no período"
                    navigation={{
                        label: "VER PERFORMANCE",
                        onClick: onNavigatePerformance,
                        visible: navigationVisibility.performance,
                    }}
                />
            </Grid>
            <Grid item xs={12} lg={6} xl={3}>
                <ANavigationTotalizer
                    loading={loading}
                    icon={<WarningIcon/>}
                    tone="error"
                    title="Alarmes Ativos"
                    value={criticalAlarms}
                    unit="críticos"
                    description={`${equipmentNoCommunication} sem comunicação`}
                    navigation={{
                        label: "VER ALARMES",
                        onClick: onNavigateAlarms,
                        visible: navigationVisibility.alarms,
                    }}
                />
            </Grid>
            <Grid item xs={12} lg={6} xl={3}>
                <ANavigationTotalizer
                    loading={loading}
                    icon={<OnlinePredictionIcon/>}
                    tone="warning"
                    title="Disponibilidade"
                    infoHint={HINT_AVAILABILITY}
                    value={availabilityPercent}
                    unit="%"
                    description="Valor total"
                    navigation={{
                        label: "VER INDISPONIBILIDADE",
                        onClick: onNavigateUnavailability,
                        visible: navigationVisibility.unavailability,
                    }}
                />
            </Grid>
        </Grid>
    );
}
